import React, { useEffect, useState } from "react";
import { Button, Card, Spinner } from "react-bootstrap";
import { useSearchParams } from "react-router-dom";
import { applyActionCode, checkActionCode } from "firebase/auth";
import styled from "styled-components";
import { auth } from "../../firebase";

const Wrapper = styled.div`
	min-height: 100vh;
	background-color: #f5f5f5;
	display: flex;
	justify-content: center;
	align-items: center;
	padding: 16px;
`;

const StyledCard = styled(Card)`
	width: 100%;
	max-width: 440px;
	border: none;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CardBody = styled(Card.Body)`
	padding: 48px 40px;
	text-align: center;
`;

const AppIcon = styled.img`
	width: 72px;
	height: 72px;
	margin-bottom: 24px;
`;

const Heading = styled.h3`
	margin-bottom: 16px;
`;

const Copy = styled.p`
	margin-bottom: 32px;
`;

const Email = styled.strong`
	display: block;
	margin-top: 8px;
	word-break: break-all;
`;

const PrimaryButton = styled(Button)`
	background-color: #004bed;
	border-color: #004bed;
	color: #ffffff;
	padding: 12px 28px;
	font-weight: 600;

	&:hover,
	&:focus,
	&:active {
		background-color: #003bc2 !important;
		border-color: #003bc2 !important;
		color: #ffffff !important;
	}

	&:disabled {
		background-color: #004bed;
		border-color: #004bed;
		opacity: 0.7;
	}
`;

type Status = "checking" | "ready" | "verifying" | "success" | "error";

const VerifyEmail: React.FC = () => {
	const [params] = useSearchParams();
	const mode = params.get("mode");
	const oobCode = params.get("oobCode");

	const [status, setStatus] = useState<Status>("checking");
	const [email, setEmail] = useState<string | null>(null);
	const [errorMessage, setErrorMessage] = useState<string>("");

	useEffect(() => {
		if (mode !== "verifyEmail" || !oobCode) {
			setStatus("error");
			setErrorMessage("This verification link is invalid.");
			return;
		}

		checkActionCode(auth, oobCode)
			.then(info => {
				if (info.operation !== "VERIFY_EMAIL") {
					setStatus("error");
					setErrorMessage(
						"This link isn't an email verification link. Please use the link from your most recent verification email.",
					);
					return;
				}
				setEmail(info.data.email ?? null);
				setStatus("ready");
			})
			.catch(err => {
				setStatus("error");
				setErrorMessage(
					err?.code === "auth/expired-action-code"
						? "This verification link has expired. Please request a new one from the app."
						: "This verification link is invalid or has already been used.",
				);
			});
	}, [mode, oobCode]);

	const handleVerify = async () => {
		if (!oobCode) return;
		setStatus("verifying");
		try {
			await applyActionCode(auth, oobCode);
			setStatus("success");
		} catch (err: any) {
			setStatus("error");
			setErrorMessage(
				err?.code === "auth/expired-action-code"
					? "This verification link has expired. Please request a new one from the app."
					: "We couldn't verify your email. The link may have already been used.",
			);
		}
	};

	return (
		<Wrapper>
			<StyledCard>
				<CardBody>
					<AppIcon src={`${process.env.PUBLIC_URL}/logo512.png`} alt="" />
					{status === "checking" && (
						<>
							<Spinner animation="border" role="status" />
							<p style={{ marginTop: 16, marginBottom: 0, color: "#6c757d" }}>
								Loading…
							</p>
						</>
					)}

					{(status === "ready" || status === "verifying") && (
						<>
							<Heading>Verify your email</Heading>
							<Copy>
								Click the button below to verify
								{email ? <Email>{email}</Email> : " your email address"}
							</Copy>
							<PrimaryButton
								onClick={handleVerify}
								disabled={status === "verifying"}>
								{status === "verifying" ? (
									<>
										<Spinner
											animation="border"
											size="sm"
											role="status"
											style={{ marginRight: 8 }}
										/>
										Verifying…
									</>
								) : (
									"Verify my email"
								)}
							</PrimaryButton>
						</>
					)}

					{status === "success" && (
						<>
							<Heading>Email verified</Heading>
							<p style={{ margin: 0 }}>
								You're all set. You can close this page and return to the app.
							</p>
						</>
					)}

					{status === "error" && (
						<>
							<Heading>Unable to verify</Heading>
							<p style={{ margin: 0 }}>{errorMessage}</p>
						</>
					)}
				</CardBody>
			</StyledCard>
		</Wrapper>
	);
};

export default VerifyEmail;
